import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import Select from './common/Select';
import { useQueryClient } from '@tanstack/react-query';
import {
  getCustomers,
  useGetCustomersIdLocations,
  usePostSlingLocationMappings,
  usePutSlingLocationMappingsId,
  useDeleteSlingLocationMappingsId,
  getGetCustomersIdQueryKey,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { SlingLocation } from '../api/generated/model/slingLocation';
import type { Customer } from '../api/generated/model';

interface SlingLocationMappingEditorModalProps {
  show: boolean;
  onHide: () => void;
  slingLocation: SlingLocation | null;
  onMappingModified?: (locationKey: string, updatedMapping: { customer_id: string; customer_name?: string | null; location?: string | null; mapping_id: string }) => void;
}

interface CustomerOption {
  value: string;
  label: string;
  address?: string;
  email?: string;
  phone?: string;
  contactName?: string;
}

interface LocationOption {
  value: string;
  label: string;
}

const SlingLocationMappingEditorModal: React.FC<SlingLocationMappingEditorModalProps> = ({
  show,
  onHide,
  slingLocation,
  onMappingModified,
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();
  const customerSelectRef = React.useRef<any>(null);

  // Fetch all customers when modal opens
  useEffect(() => {
    if (!show) return;

    const fetchAllCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const allCustomers: Customer[] = [];
        let cursor: string | undefined = undefined;
        let hasMore = true;

        while (hasMore) {
          const response: Awaited<ReturnType<typeof getCustomers>> = await getCustomers(cursor ? {
            limit: 100,
            cursor,
          } : {
            limit: 100,
            sort_field: 'name' as any,
            sort_direction: 'asc',
          });

          if (response.data) {
            allCustomers.push(...response.data);
          }

          hasMore = !!response.pagination?.cursor;
          cursor = response.pagination?.cursor || undefined;
        }

        setCustomers(allCustomers);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError('Failed to load customers');
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchAllCustomers();
  }, [show]);

  // Fetch locations for selected customer
  const { data: locationsData } = useGetCustomersIdLocations(
    selectedCustomerId || '',
    { query: { enabled: !!selectedCustomerId } }
  );

  // Mutations
  const createMapping = usePostSlingLocationMappings();
  const updateMapping = usePutSlingLocationMappingsId();
  const deleteMapping = useDeleteSlingLocationMappingsId();

  // Initialize form when modal opens with existing mapping
  useEffect(() => {
    if (!show || !slingLocation) {
      setSelectedCustomerId(null);
      setSelectedLocation(null);
      setError(null);
      setIsSaving(false); // Reset saving state when modal closes
      return;
    }

    // Reset state when modal opens
    setIsSaving(false);

    // If there's an existing mapping, set it
    if (slingLocation.mapped_to_customer_id) {
      setSelectedCustomerId(slingLocation.mapped_to_customer_id);
      setSelectedLocation(slingLocation.mapped_to_location || '');
    } else {
      setSelectedCustomerId(null);
      setSelectedLocation(null);
    }
  }, [show, slingLocation]);

  // Focus the customer select when modal opens
  useEffect(() => {
    if (show && customerSelectRef.current) {
      // Small delay to ensure modal animation completes
      const timer = setTimeout(() => {
        customerSelectRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Format address from customer source
  const formatAddress = (customer: Customer): string | undefined => {
    if (!customer.primary_source?.address) return undefined;

    const addr = customer.primary_source.address;
    const parts = [
      addr.line1,
      addr.line2,
      addr.city,
      [addr.state, addr.zip].filter(Boolean).join(' '),
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : undefined;
  };

  // Customer options for dropdown
  const customerOptions: CustomerOption[] = useMemo(() => {
    if (!customers.length) return [];

    return customers
      .map(customer => ({
        value: customer.id || '',
        label: customer.name || 'Unknown',
        address: formatAddress(customer),
        email: customer.primary_source?.contact?.email,
        phone: customer.primary_source?.contact?.phone,
        contactName: customer.primary_source?.contact?.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  // Location options for selected customer
  const locationOptions: LocationOption[] = useMemo(() => {
    const options: LocationOption[] = [
      { value: '', label: 'Primary/Default' }
    ];

    if (locationsData?.data) {
      const sortedLocations = [...locationsData.data]
        .filter((loc): loc is string => loc !== null && loc !== '')
        .sort();

      sortedLocations.forEach(loc => {
        options.push({ value: loc, label: loc });
      });
    }

    return options;
  }, [locationsData]);

  const handleCustomerChange = (option: CustomerOption | null) => {
    setSelectedCustomerId(option?.value || null);
    setSelectedLocation(''); // Reset to default when customer changes
    setError(null);
  };

  const handleLocationChange = (option: LocationOption | null) => {
    setSelectedLocation(option?.value || '');
    setError(null);
  };

  const handleSave = async () => {
    if (!slingLocation || !selectedCustomerId) {
      setError('Please select a customer');
      return;
    }

    setError(null);
    setIsSaving(true);

    const locationKey = `${slingLocation.src_id}-${slingLocation.id}`;

    try {
      const mappingData = {
        customer_id: selectedCustomerId,
        sling_location_src_id: slingLocation.src_id || '',
        sling_location_id: slingLocation.id || 0,
        location: selectedLocation || undefined,
      };

      let result;
      if (slingLocation.mapped_to_mapping_id) {
        result = await updateMapping.mutateAsync({
          id: slingLocation.mapped_to_mapping_id,
          data: mappingData,
        });
      } else {
        result = await createMapping.mutateAsync({
          data: mappingData,
        });
      }

      // Extract the updated mapping info from the result
      // Get customer name from our local customers list
      const customer = customers.find(c => c.id === selectedCustomerId);
      const updatedMapping = {
        customer_id: result.customer_id,
        customer_name: customer?.name || null,
        location: result.location || null,
        mapping_id: result.id,
      };

      // Notify parent with the updated data - it will update local state immediately
      if (onMappingModified && slingLocation) {
        onMappingModified(locationKey, updatedMapping);
      }

      // Close modal
      onHide();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Failed to save mapping';
      setError(errorMessage);
      setIsSaving(false); // Only reset on error so user can retry
    }
  };

  const handleDelete = async () => {
    if (!slingLocation?.mapped_to_mapping_id) return;

    setError(null);
    setIsSaving(true);

    const locationKey = `${slingLocation.src_id}-${slingLocation.id}`;

    try {
      await deleteMapping.mutateAsync({
        id: slingLocation.mapped_to_mapping_id,
      });

      // Notify parent with null mapping (deleted)
      if (onMappingModified) {
        const updatedMapping = {
          customer_id: '',
          customer_name: null,
          location: null,
          mapping_id: '',
        };
        onMappingModified(locationKey, updatedMapping);
      }

      // Close modal
      onHide();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Failed to delete mapping';
      setError(errorMessage);
      setIsSaving(false); // Only reset on error so user can retry
    }
  };

  const currentCustomer = customerOptions.find(opt => opt.value === selectedCustomerId) || null;
  const currentLocation = locationOptions.find(opt => opt.value === selectedLocation) || null;

  const hasExistingMapping = !!slingLocation?.mapped_to_mapping_id;
  const hasChanges = hasExistingMapping
    ? (selectedCustomerId !== slingLocation?.mapped_to_customer_id ||
       selectedLocation !== (slingLocation?.mapped_to_location || ''))
    : selectedCustomerId !== null;

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Enter key - save/create mapping
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Check if a dropdown is open (react-select adds specific classes/attributes)
        const isMenuOpen = document.querySelector('.select__menu');
        if (isMenuOpen) {
          // Let react-select handle Enter when menu is open
          return;
        }

        // Only save if button would be enabled
        if (selectedCustomerId && hasChanges) {
          e.preventDefault();
          handleSave();
        }
      }
      // Escape key - cancel/close
      else if (e.key === 'Escape') {
        e.preventDefault();
        onHide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, selectedCustomerId, selectedLocation, hasChanges, handleSave, onHide]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Map Sling Location: {slingLocation?.name || 'Unknown'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </Alert>
        )}

        <Alert variant="info" className="mb-3">
          <strong>Sling Location Details:</strong>
          <br />
          Name: <strong>{slingLocation?.name}</strong>
          <br />
          State: <strong>{slingLocation?.state || 'N/A'}</strong>
          <br />
          ID: <strong>{slingLocation?.id}</strong>
          <br />
          Address: <strong>{slingLocation?.address || 'N/A'}</strong>
          <br />
          Timezone: <strong>{slingLocation?.timezone || 'N/A'}</strong>
          <br />
          Shifts: <strong>{slingLocation?.shift_count || 0}</strong>
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Customer</Form.Label>
            <Select<CustomerOption>
              ref={customerSelectRef}
              value={currentCustomer}
              onChange={handleCustomerChange}
              options={customerOptions}
              isClearable
              isLoading={loadingCustomers}
              placeholder={loadingCustomers ? "Loading customers..." : "Select customer..."}
              formatOptionLabel={(option) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{option.label}</div>
                  {option.address && <div className="text-muted small">{option.address}</div>}
                  {option.contactName && <div className="text-muted small">{option.contactName}</div>}
                  {option.email && <div className="text-muted small">{option.email}</div>}
                  {option.phone && <div className="text-muted small">{option.phone}</div>}
                </div>
              )}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Customer Location</Form.Label>
            <Select<LocationOption>
              value={currentLocation}
              onChange={handleLocationChange}
              options={locationOptions}
              isDisabled={!selectedCustomerId}
              placeholder={selectedCustomerId ? "Select location..." : "Select a customer first..."}
            />
            <Form.Text className="text-muted">
              Select "Primary/Default" if this Sling location doesn't map to a specific customer location.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {hasExistingMapping && (
            <Button variant="danger" onClick={handleDelete} disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>
                  Delete Mapping
                </>
              )}
            </Button>
          )}
        </div>
        <div>
          <Button variant="secondary" onClick={onHide} className="me-2" disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedCustomerId || !hasChanges || isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-save me-2"></i>
                {hasExistingMapping ? 'Update Mapping' : 'Create Mapping'}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SlingLocationMappingEditorModal;
